/**
 * Typed fetchers for the /api/creations endpoints.
 *
 * This file is intentionally isolated from lib/creations.ts (the localStorage
 * layer). The hook layer (useCreations.ts) is the single bridge that converts
 * server-shaped rows into the local Creation shape consumed by the dashboard.
 */

import { z } from "zod";
import { api } from "./client";
import { CREATION_TYPES } from "@/lib/creations";
import type { CreationType, CreationMigrationItem } from "@/lib/creations";
export type { CreationMigrationItem };

// ---------------------------------------------------------------------------
// Server-side shapes (mirror src/api/schemas/creations.py)
// ---------------------------------------------------------------------------

export const CreationServerRowSchema = z.object({
  id: z.number(),
  type: z.enum(CREATION_TYPES),
  title: z.string(),
  tool_path: z.string(),
  content: z.string(),
  inputs_json: z.record(z.unknown()).nullable(),
  created_at: z.string(),
});

export type CreationServerRow = z.infer<typeof CreationServerRowSchema>;

export type CreationCreatePayload = {
  type: CreationType;
  title: string;
  tool_path: string;
  content: string;
  inputs_json?: Record<string, unknown>;
  client_id?: string;
};

export type CreationMigrationResponse = {
  migrated: number;
  skipped: number;
};

const CreationServerRowListSchema = z.array(CreationServerRowSchema);

const CreationMigrationResponseSchema = z.object({
  migrated: z.number(),
  skipped: z.number(),
});

// ---------------------------------------------------------------------------
// Fetchers
// ---------------------------------------------------------------------------

/** GET /api/creations — returns newest-first, filtered by optional params. */
export async function listCreations(params?: {
  type?: CreationType;
  before?: string;
  limit?: number;
}): Promise<CreationServerRow[]> {
  const qs = new URLSearchParams();
  if (params?.type) qs.set("type", params.type);
  if (params?.before) qs.set("before", params.before);
  if (params?.limit != null) qs.set("limit", String(params.limit));
  const query = qs.toString();
  const path = query ? `/api/creations?${query}` : "/api/creations";
  return api.get(path, CreationServerRowListSchema);
}

/** POST /api/creations — idempotent on (user_id, client_id) collision. */
export async function createCreation(
  body: CreationCreatePayload,
): Promise<CreationServerRow> {
  return api.post("/api/creations", body, CreationServerRowSchema);
}

/** DELETE /api/creations/{id} — 204 on success, 404 for cross-user access. */
export async function deleteCreation(id: number): Promise<void> {
  return api.delete(`/api/creations/${id}`);
}

/**
 * POST /api/creations/migrate — batch-upsert localStorage rows to the server.
 * The server dedups via the partial unique index on (user_id, client_id).
 */
export async function migrateCreations(
  items: CreationMigrationItem[],
): Promise<CreationMigrationResponse> {
  return api.post("/api/creations/migrate", { items }, CreationMigrationResponseSchema);
}
